/**
 * Group Details Widget - Embeddable Bundle Entry Point
 *
 * One page for woodsidebible.org/group-finder/details/?id=<Group_ID>,
 * replacing the old `group-details.vercel.app` CustomWidget and the stock
 * `<mpp-group-details>` sign-up widget that sat under it. Same shadow-DOM
 * shell as the finder (shell.tsx), same API, same components.
 *
 * Embed:
 *   <div id="groupDetails"></div>
 *   <script src="https://widgets.woodsidebible.org/group-finder/details.js"></script>
 *
 * The group comes from `data-group` or, by default, the page's `?id=` query
 * param (what the finder links to and what the old page used).
 */

import React from 'react';
import { GroupDetails } from '../src/components/GroupDetails';
import { attr } from './params';
import { ShadowWidget } from './shell';
import { DEFAULT_ASSET_BASE, setSeeJesusAssetBase } from '../src/lib/seeJesus';

interface WidgetConfig {
  apiBaseUrl?: string;
  /** Where the widget's public assets live (default: widgets-host) */
  assetBaseUrl?: string;
  containerId?: string;
  groupId?: number;
  finderUrl?: string;
  inquiryFormUrl?: string;
}

declare global {
  interface Window {
    GROUP_DETAILS_WIDGET_CONFIG?: WidgetConfig;
    GroupDetailsWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'group-details-root';
const LEGACY_CONTAINER_ID = 'groupDetails';

function groupIdFromPage(container: HTMLElement, config: WidgetConfig): number | null {
  const fromAttr = attr(container.dataset.group);
  let raw: string | undefined = config.groupId ? String(config.groupId) : fromAttr;
  if (!raw) {
    try {
      raw = new URLSearchParams(window.location.search).get('id') ?? undefined;
    } catch {
      /* no URL API */
    }
  }
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function createDetailsWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  const assetBaseUrl = (config.assetBaseUrl || DEFAULT_ASSET_BASE).replace(/\/$/, '');
  setSeeJesusAssetBase(assetBaseUrl);
  return new ShadowWidget({
    assetBaseUrl,
    containerIds: config.containerId
      ? [config.containerId]
      : [DEFAULT_CONTAINER_ID, LEGACY_CONTAINER_ID],
    overlayHostId: 'group-details-overlay',
    fontId: 'group-finder-widget-montserrat',
    scrollLockFlag: '__GROUP_DETAILS_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Group Details Widget',
    render: (container) => (
      <GroupDetails
        apiBaseUrl={apiBaseUrl}
        groupId={groupIdFromPage(container, config)}
        finderUrl={config.finderUrl ?? attr(container.dataset.finderUrl)}
        inquiryFormUrl={config.inquiryFormUrl ?? attr(container.dataset.inquiryUrl)}
      />
    ),
  });
}

if (typeof window !== 'undefined') {
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.GROUP_DETAILS_WIDGET_CONFIG || {}),
  };
  window.GROUP_DETAILS_WIDGET_CONFIG = config;

  if (!window.GroupDetailsWidget) {
    const boot = () => {
      const widget = createDetailsWidget(config);
      widget.init();
      window.GroupDetailsWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
